import { Routes, Route } from 'react-router-dom';
import { AdminShell } from './AdminShell';
import { RouteGuard, LandingRedirect } from './RouteGuard';
import { Dashboard } from '@/pages/admin/Dashboard';
import { Customers } from '@/pages/admin/Customers';
import { Drivers } from '@/pages/admin/Drivers';
import { Trips } from '@/pages/admin/Trips';
import { ReserveTrips } from '@/pages/admin/ReserveTrips';
import { BoatTrips } from '@/pages/admin/BoatTrips';
import { Wallet } from '@/pages/admin/Wallet';
import { WalletApprovals } from '@/pages/admin/WalletApprovals';
import { Payments } from '@/pages/admin/Payments';
import { DriverTips } from '@/pages/admin/DriverTips';
import { DriverExpenses } from '@/pages/admin/DriverExpenses';
import { Payouts } from '@/pages/admin/Payouts';
import { PayoutKYC } from '@/pages/admin/PayoutKYC';
import { PayoutKycReview } from '@/pages/admin/PayoutKycReview';
import { DriverEarnings } from '@/pages/admin/DriverEarnings';
import { FareRates } from '@/pages/admin/FareRates';
import { DestinationSettings } from '@/pages/admin/DestinationSettings';
import { FeatureSettings } from '@/pages/admin/FeatureSettings';
import { MembershipTiers } from '@/pages/admin/MembershipTiers';
import { Chargers } from '@/pages/admin/Chargers';
import { Speeding } from '@/pages/admin/Speeding';
import { Complaints } from '@/pages/admin/Complaints';
import { Support } from '@/pages/admin/Support';
import { Incentives } from '@/pages/admin/Incentives';
import { Quests } from '@/pages/admin/Quests';
import { QuestTiers } from '@/pages/admin/QuestTiers';
import { QuestOptins } from '@/pages/admin/QuestOptins';
import { GuaranteeEnrollments } from '@/pages/admin/GuaranteeEnrollments';
import { Streaks } from '@/pages/admin/Streaks';
import { Boosts } from '@/pages/admin/Boosts';
import { Promos } from '@/pages/admin/Promos';
import { Content } from '@/pages/admin/Content';
import { Zones } from '@/pages/admin/Zones';
import { Docks } from '@/pages/admin/Docks';
import { TripTypes } from '@/pages/admin/TripTypes';
import { VehicleCategories } from '@/pages/admin/VehicleCategories';
import { ComplaintCategories } from '@/pages/admin/ComplaintCategories';
import { BookingStatuses } from '@/pages/admin/BookingStatuses';
import { Notifications } from '@/pages/admin/Notifications';
import { Safety } from '@/pages/admin/Safety';
import { CustomerIdentity } from '@/pages/admin/CustomerIdentity';
import { Staff } from '@/pages/staff-mgmt';
import { Roles } from '@/pages/admin/Roles';
import { Audit } from '@/pages/admin/Audit';
import { OtpCodes } from '@/pages/admin/OtpCodes';
import { PushLog } from '@/pages/admin/PushLog';
import { VerificationLog } from '@/pages/admin/VerificationLog';

/**
 * The Admin app: the shell (sidebar + identity + logout) wrapping the routed
 * Admin pages. The staff-auth gate (AdminAuthGate) is mounted ONE level up in
 * main.tsx, so by the time this renders the staffer is authenticated.
 *
 * Routes live at the ROOT ('/customers', '/drivers', …) — this is a dedicated
 * admin portal, not a sub-app under '/admin'. The Dashboard is the default
 * landing (the index route) and also has its own '/dashboard' path; unknown
 * paths fall back to it.
 */
export function AdminApp() {
  return (
    <AdminShell>
      <Routes>
        <Route index element={<LandingRedirect />} />
        <Route path="dashboard" element={<RouteGuard to="/dashboard"><Dashboard /></RouteGuard>} />
        <Route path="customers" element={<RouteGuard to="/customers"><Customers /></RouteGuard>} />
        <Route path="customers/identity" element={<RouteGuard to="/customers/identity"><CustomerIdentity /></RouteGuard>} />
        <Route path="drivers" element={<RouteGuard to="/drivers"><Drivers /></RouteGuard>} />
        <Route path="trips" element={<RouteGuard to="/trips"><Trips /></RouteGuard>} />
        <Route path="reserve-trips" element={<RouteGuard to="/reserve-trips"><ReserveTrips /></RouteGuard>} />
        <Route path="boat-trips" element={<RouteGuard to="/boat-trips"><BoatTrips /></RouteGuard>} />
        <Route path="wallet" element={<RouteGuard to="/wallet"><Wallet /></RouteGuard>} />
        <Route path="wallet-approvals" element={<RouteGuard to="/wallet-approvals"><WalletApprovals /></RouteGuard>} />
        <Route path="payments" element={<RouteGuard to="/payments"><Payments /></RouteGuard>} />
        <Route path="driver-tips" element={<RouteGuard to="/driver-tips"><DriverTips /></RouteGuard>} />
        <Route path="driver-expenses" element={<RouteGuard to="/driver-expenses"><DriverExpenses /></RouteGuard>} />
        <Route path="payouts" element={<RouteGuard to="/payouts"><Payouts /></RouteGuard>} />
        <Route path="payout-kyc" element={<RouteGuard to="/payout-kyc"><PayoutKYC /></RouteGuard>} />
        <Route path="payout-kyc-review" element={<RouteGuard to="/payout-kyc-review"><PayoutKycReview /></RouteGuard>} />
        <Route path="driver-earnings" element={<RouteGuard to="/driver-earnings"><DriverEarnings /></RouteGuard>} />
        <Route path="fares" element={<RouteGuard to="/fares"><FareRates /></RouteGuard>} />
        <Route path="destination-settings" element={<RouteGuard to="/destination-settings"><DestinationSettings /></RouteGuard>} />
        <Route path="operations" element={<RouteGuard to="/operations"><FeatureSettings /></RouteGuard>} />
        <Route path="membership-tiers" element={<RouteGuard to="/membership-tiers"><MembershipTiers /></RouteGuard>} />
        <Route path="chargers" element={<RouteGuard to="/chargers"><Chargers /></RouteGuard>} />
        <Route path="speeding" element={<RouteGuard to="/speeding"><Speeding /></RouteGuard>} />
        <Route path="complaints" element={<RouteGuard to="/complaints"><Complaints /></RouteGuard>} />
        <Route path="support" element={<RouteGuard to="/support"><Support /></RouteGuard>} />
        <Route path="incentives" element={<RouteGuard to="/incentives"><Incentives /></RouteGuard>} />
        <Route path="quests" element={<RouteGuard to="/quests"><Quests /></RouteGuard>} />
        <Route path="quest-tiers" element={<RouteGuard to="/quest-tiers"><QuestTiers /></RouteGuard>} />
        <Route path="quest-optins" element={<RouteGuard to="/quest-optins"><QuestOptins /></RouteGuard>} />
        <Route path="guarantee-enrollments" element={<RouteGuard to="/guarantee-enrollments"><GuaranteeEnrollments /></RouteGuard>} />
        <Route path="streaks" element={<RouteGuard to="/streaks"><Streaks /></RouteGuard>} />
        <Route path="boosts" element={<RouteGuard to="/boosts"><Boosts /></RouteGuard>} />
        <Route path="promos" element={<RouteGuard to="/promos"><Promos /></RouteGuard>} />
        <Route path="content" element={<RouteGuard to="/content"><Content /></RouteGuard>} />
        <Route path="zones" element={<RouteGuard to="/zones"><Zones /></RouteGuard>} />
        <Route path="docks" element={<RouteGuard to="/docks"><Docks /></RouteGuard>} />
        <Route path="trip-types" element={<RouteGuard to="/trip-types"><TripTypes /></RouteGuard>} />
        <Route path="vehicle-categories" element={<RouteGuard to="/vehicle-categories"><VehicleCategories /></RouteGuard>} />
        <Route path="complaint-categories" element={<RouteGuard to="/complaint-categories"><ComplaintCategories /></RouteGuard>} />
        <Route path="booking-statuses" element={<RouteGuard to="/booking-statuses"><BookingStatuses /></RouteGuard>} />
        <Route path="notifications" element={<RouteGuard to="/notifications"><Notifications /></RouteGuard>} />
        <Route path="safety" element={<RouteGuard to="/safety"><Safety /></RouteGuard>} />
        <Route path="staff" element={<RouteGuard to="/staff"><Staff /></RouteGuard>} />
        <Route path="roles" element={<RouteGuard to="/roles"><Roles /></RouteGuard>} />
        <Route path="audit" element={<RouteGuard to="/audit"><Audit /></RouteGuard>} />
        <Route path="otp-codes" element={<RouteGuard to="/otp-codes"><OtpCodes /></RouteGuard>} />
        <Route path="push-log" element={<RouteGuard to="/push-log"><PushLog /></RouteGuard>} />
        <Route path="verification-log" element={<RouteGuard to="/verification-log"><VerificationLog /></RouteGuard>} />
        <Route path="*" element={<LandingRedirect />} />
      </Routes>
    </AdminShell>
  );
}
